import React, { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, FileText, Download, Loader2 } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { useAuth } from '../../contexts/AuthContext'
import { supabase } from '../../lib/supabaseClient'
import { presetRange } from '../../lib/periodReport'
import { buildPeriodReportHtml, suggestedReportFilenameHtml } from '../../lib/periodReportHtml'
import { useProfile } from '../../hooks/useProfile'
import { isOwner } from '../../lib/owner'
import { performRefresh } from '../../lib/refresh'
import PullToRefresh from '../../components/PullToRefresh'
import ReportPreviewModal from './components/ReportPreviewModal'
import { cn } from '../../lib/utils'

const PRESETS = ['thisWeek', 'last7', 'thisMonth', 'last30', 'custom']

export function ReportsPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { profile } = useProfile()
  const goBack = () => navigate('/')

  const [preset, setPreset] = useState('thisWeek')
  const [{ start, end }, setRange] = useState(() => presetRange('thisWeek'))
  const [generating, setGenerating] = useState(false)
  const [lastError, setLastError] = useState(null)
  const [preview, setPreview] = useState(null) // { html, filename } | null

  const handlePreset = (p) => {
    setPreset(p)
    if (p !== 'custom') {
      setRange(presetRange(p))
    }
  }

  const handleGenerate = async () => {
    if (!user || generating) return
    setGenerating(true)
    setLastError(null)
    try {
      const startEndDate = end
      // Parallel queries — one round-trip per module. Keeps the wall
      // time short on slow networks (≈the slowest query, not the sum).
      const ownerFlag = isOwner(user)
      // Every module the app tracks gets its own query so the report
      // can render the full picture. Each one degrades to [] on
      // PGRST205 (table missing) — see soft-error handling below.
      const [
        sessionsRes,
        weightsRes,
        watersRes,
        mealsRes,
        medsRes,
        suppsRes,
        condsRes,
        intakeRes,
        vitalsRes,
        balanceRes,
        targetsRes,
        incomeRes,
        fixedRes,
        variableRes,
        installmentsRes,
        specialRes,
        phasesRes,
        recoveryUrgesRes,
        recoveryCheckinsRes,
        learningRes,
      ] = await Promise.all([
        supabase.from('sessions').select(`
          id, date, session_type, duration_min, body_weight_kg, notes,
          exercise_logs(id, order_index, notes, exercises:exercise_id(name, muscle_group), sets(set_number, weight_kg, reps, rpe, is_warmup, is_failure)),
          cardio_logs(cardio_type, duration_min, level, incline, speed, distance_km, calories_estimated)
        `).gte('date', start).lte('date', startEndDate).order('date', { ascending: true }),
        supabase.from('weight_entries').select('date, weight').gte('date', start).lte('date', end),
        supabase.from('water_entries').select('date, amount_ml').gte('date', start).lte('date', end),
        supabase.from('meal_entries').select('date, kcal, protein_g, carbs_g, fat_g').gte('date', start).lte('date', end),
        supabase.from('medications').select('id, name, dosage, frequency, schedule_times, started_at, stopped_at, reminders_enabled, notes'),
        supabase.from('supplements').select('id, name, dosage, frequency, schedule_times, started_at, stopped_at, reminders_enabled, notes'),
        supabase.from('health_conditions').select('id, name, diagnosed_at, doctor, is_active, notes'),
        supabase.from('intake_log').select('item_kind, item_id, date, scheduled_time').gte('date', start).lte('date', end),
        supabase.from('vital_entries').select('kind, measured_at, systolic, diastolic, bpm, glucose_mg_dl, context').gte('measured_at', start).lte('measured_at', `${end}T23:59:59`),
        supabase.from('balance_entries').select('amount, as_of_date, notes'),
        supabase.from('financial_targets').select('name, type, target_value, target_date, status, achieved_at, notes'),
        supabase.from('income_sources').select('name, amount, frequency, status, notes'),
        supabase.from('fixed_expenses').select('name, amount, category, status, notes'),
        supabase.from('variable_expenses').select('monthly_estimate, notes').maybeSingle(),
        supabase.from('installments').select('provider, label, monthly_amount, total_months, remaining_months, status, notes'),
        supabase.from('special_obligations').select('name, kind, amount, frequency, status, notes'),
        supabase.from('phases').select('name, phase_type, start_date, end_date, start_weight_kg, goal_weight_kg, notes').order('start_date', { ascending: false }),
        ownerFlag
          ? supabase.from('recovery_urges').select('recorded_at, intensity, trigger_text, response, notes').gte('recorded_at', start).lte('recorded_at', `${end}T23:59:59`)
          : Promise.resolve({ data: [], error: null }),
        ownerFlag
          ? supabase.from('recovery_checkins').select('date, mood, energy, urges_today, notes').gte('date', start).lte('date', end)
          : Promise.resolve({ data: [], error: null }),
        supabase.from('learning_resources').select('topic, title, url, type, completed, added_at'),
      ])

      // Tolerant of missing tables (e.g. when migration 017 / 018 haven't
      // been applied yet). Each query independently degrades to []
      // for that module's section. A missing-table soft warning lets
      // the user know which migrations to run for full coverage,
      // without blocking the rest of the report.
      const namedResults = [
        ['sessions', sessionsRes],
        ['weight_entries', weightsRes],
        ['water_entries', watersRes],
        ['meal_entries', mealsRes],
        ['medications', medsRes],
        ['supplements', suppsRes],
        ['health_conditions', condsRes],
        ['intake_log', intakeRes],
        ['vital_entries', vitalsRes],
        ['balance_entries', balanceRes],
        ['financial_targets', targetsRes],
        ['income_sources', incomeRes],
        ['fixed_expenses', fixedRes],
        ['variable_expenses', variableRes],
        ['installments', installmentsRes],
        ['special_obligations', specialRes],
        ['phases', phasesRes],
        ['recovery_urges', recoveryUrgesRes],
        ['recovery_checkins', recoveryCheckinsRes],
        ['learning_resources', learningRes],
      ]
      const missingTables = []
      for (const [name, res] of namedResults) {
        if (res.error) {
          console.error(`[report] ${name} fetch failed:`, res.error)
          // PGRST205 = "table not found in schema cache" — soft warning.
          // Other errors stay silent in the file but logged in console;
          // they shouldn't kill the whole report.
          if (
            res.error.code === 'PGRST205'
            || /schema cache/i.test(res.error.message || '')
            || /does not exist/i.test(res.error.message || '')
          ) {
            missingTables.push(name)
          }
        }
      }

      // Group vitals by kind so the section builder just iterates.
      // res.data is null on error → fall through as empty.
      const vitalsByKind = {}
      for (const v of vitalsRes.data || []) {
        if (!vitalsByKind[v.kind]) vitalsByKind[v.kind] = []
        vitalsByKind[v.kind].push(v)
      }

      if (missingTables.length > 0) {
        setLastError(
          t('reports.missingTables', { tables: missingTables.join(', ') }),
        )
      }

      // Use the profile name only — never the email. If the user
      // hasn't set a name, the cover just omits that row.
      const userName = (profile?.name || '').trim()
      const html = buildPeriodReportHtml({
        startIso: start,
        endIso: end,
        language: i18n.language,
        userName,
        includeJourney: ownerFlag,
        data: {
          sessions: sessionsRes.data || [],
          weights: weightsRes.data || [],
          waters: watersRes.data || [],
          meals: mealsRes.data || [],
          medications: medsRes.data || [],
          supplements: suppsRes.data || [],
          conditions: condsRes.data || [],
          intakeLog: intakeRes.data || [],
          vitalsByKind,
          balanceEntries: balanceRes.data || [],
          targets: targetsRes.data || [],
          incomeSources: incomeRes.data || [],
          fixedExpenses: fixedRes.data || [],
          variableExpense: variableRes.data || null,
          installments: installmentsRes.data || [],
          specialObligations: specialRes.data || [],
          phases: phasesRes.data || [],
          recoveryUrges: recoveryUrgesRes.data || [],
          recoveryCheckins: recoveryCheckinsRes.data || [],
          learningResources: learningRes.data || [],
          waterGoalMl: profile?.water_goal_ml,
        },
      })
      setPreview({
        html,
        filename: suggestedReportFilenameHtml(i18n.language, start, end),
      })
    } finally {
      setGenerating(false)
    }
  }

  const handleRefresh = useCallback(() => performRefresh([]), [])

  return (
    <PullToRefresh onRefresh={handleRefresh}>
    <div className="space-y-4 content-fade pb-8">
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={goBack}
          aria-label={t('settings.back')}
          className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
        >
          <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-foreground flex items-center gap-2" dir="auto">
            <FileText className="w-5 h-5 text-app-primary-500" />
            {t('reports.title')}
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5" dir="auto">
            {t('reports.subtitle')}
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="p-3 sm:p-4 space-y-4">
          <div>
            <p className="text-xs text-muted-foreground mb-2" dir="auto">
              {t('reports.preset')}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => handlePreset(p)}
                  aria-pressed={preset === p}
                  className={cn(
                    'px-3 py-2 rounded-md border text-sm transition-colors',
                    preset === p
                      ? 'border-app-primary-500 bg-app-primary-500/10 text-app-primary-500'
                      : 'border-input text-muted-foreground hover:bg-accent',
                  )}
                >
                  {t(`reports.presets.${p}`)}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-muted-foreground mb-1" dir="auto">
                {t('reports.startDate')}
              </label>
              <input
                type="date"
                value={start}
                onChange={(e) => {
                  setRange((r) => ({ ...r, start: e.target.value }))
                  setPreset('custom')
                }}
                dir="ltr"
                className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1" dir="auto">
                {t('reports.endDate')}
              </label>
              <input
                type="date"
                value={end}
                onChange={(e) => {
                  setRange((r) => ({ ...r, end: e.target.value }))
                  setPreset('custom')
                }}
                dir="ltr"
                className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm font-mono"
              />
            </div>
          </div>

          <Button
            onClick={handleGenerate}
            disabled={generating || !start || !end || start > end}
            className="w-full"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 me-1.5 animate-spin" />
            ) : (
              <Download className="w-4 h-4 me-1.5" />
            )}
            {t('reports.generate')}
          </Button>

          {lastError && (
            <p className="text-xs text-red-600" dir="auto">{lastError}</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-3 sm:p-4 text-xs text-muted-foreground space-y-1" dir="auto">
          <p>{t('reports.includes')}</p>
          <ul className="list-disc list-inside space-y-0.5">
            <li>{t('reports.includesWorkout')}</li>
            <li>{t('reports.includesPhoenix')}</li>
            <li>{t('reports.includesHealth')}</li>
            <li>{t('reports.includesVitals')}</li>
            <li>{t('reports.includesFinance')}</li>
            <li>{t('reports.includesPhases')}</li>
            <li>{t('reports.includesLearning')}</li>
            {isOwner(user) && <li>{t('reports.includesJourney')}</li>}
          </ul>
          <p className="pt-1.5 text-[11px] opacity-80">{t('reports.formatHint')}</p>
        </CardContent>
      </Card>
    </div>
    {preview && (
      <ReportPreviewModal
        html={preview.html}
        filename={preview.filename}
        onClose={() => setPreview(null)}
      />
    )}
    </PullToRefresh>
  )
}
